import CustomReportWebApi from '../service/persistent/CustomReportWebApi';
import Store from 'repatch';
import State from '../core/State';
import {
  disposeCustomReports,
  startFetchingCustomReports,
  finishFetchingCustomReports,
  setCustomReports,
} from '../thunk/customReport';
import ApiErrorHandler from '../service/ApiErrorHandler';
import { setFromPeriod, setToPeriod } from '../thunk/customReportChartSetting';
import { DateTime } from 'luxon';
import { createPath, History } from 'history';
import { setSelectedChartGroup } from '../thunk/selectedChartGroup';
import mergeUrlQuery from '../utility/mergeUrlQuery';

class CustomReportUsecase {
  private store: Store<State>;
  private history: History;
  private customReportWebApi: CustomReportWebApi;
  private apiErrorHandler: ApiErrorHandler;

  constructor({
    store,
    history,
    customReportWebApi,
    apiErrorHandler,
  }: {
    store: Store<State>;
    history: History;
    customReportWebApi: CustomReportWebApi;
    apiErrorHandler: ApiErrorHandler;
  }) {
    this.store = store;
    this.history = history;
    this.customReportWebApi = customReportWebApi;
    this.apiErrorHandler = apiErrorHandler;
  }

  public prepareCustomReport = (): void => {
    this.getCustomReport();
  };

  public setFromPeriod = (date: DateTime | null): void => {
    if (date) {
      this.store.dispatch(setFromPeriod(date));
      this.setFromToURL();
    }
  };

  public setToPeriod = (date: DateTime | null): void => {
    if (date) {
      this.store.dispatch(setToPeriod(date));
      this.setToToURL();
    }
  };

  public setSelectedChartGroup = (data: number): void => {
    this.store.dispatch(setSelectedChartGroup(data));
    this.setSelectedChartGroupToURL();
  };

  public dispose = (): void => {
    this.store.dispatch(disposeCustomReports());
  };

  private getCustomReport = async (): Promise<void> => {
    this.store.dispatch(disposeCustomReports());
    this.store.dispatch(startFetchingCustomReports());

    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;

    const query = new URLSearchParams(state.location.search);
    const fromParam = query.get('from'),
      toParam = query.get('to');

    const from =
      fromParam && DateTime.fromFormat(fromParam, 'yyyyMMdd', { zone: 'utc' }).toISO()
        ? DateTime.fromFormat(fromParam, 'yyyyMMdd', { zone: 'utc' }).toISO()
        : DateTime.local().startOf('month').toISO();
    const to =
      toParam && DateTime.fromFormat(toParam, 'yyyyMMdd', { zone: 'utc' }).toISO()
        ? DateTime.fromFormat(toParam, 'yyyyMMdd', { zone: 'utc' }).toISO()
        : DateTime.local().endOf('month').toISO();

    const group = query.get('group') ? Number(query.get('group')) : 0;

    this.store.dispatch(setFromPeriod(DateTime.fromISO(from)));
    this.store.dispatch(setToPeriod(DateTime.fromISO(to)));
    try {
      // const customReport = await this.customReportWebApi.customReportStub();
      const customReport = await this.customReportWebApi.customReport(organizationId);
      if (customReport !== null) {
        this.store.dispatch(setCustomReports(customReport));

        if (customReport.groups[group]) {
          this.store.dispatch(setSelectedChartGroup(group as number));
        }
      }
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingCustomReports());
      this.setFromToURL();
      this.setToToURL();
      this.setSelectedChartGroupToURL();
    }
  };

  public setFromToURL = async (): Promise<void> => {
    const state = this.store.getState();
    const from = state.customReportChartSetting.period.from;

    this.history.push(createPath(mergeUrlQuery(this.history.location, { from: from.toFormat('yyyyMMdd') })));
  };

  public setToToURL = async (): Promise<void> => {
    const state = this.store.getState();
    const to = state.customReportChartSetting.period.to;

    this.history.push(createPath(mergeUrlQuery(this.history.location, { to: to.toFormat('yyyyMMdd') })));
  };

  public setSelectedChartGroupToURL = async (): Promise<void> => {
    const state = this.store.getState();
    const chartGroupNum = state.selectedChartGroup.data;

    this.history.push(createPath(mergeUrlQuery(this.history.location, { group: chartGroupNum.toString() })));
  };
}

export default CustomReportUsecase;
